import * as _ from "lodash";
import { IdentityProvider } from "../PA.Core/Basic/Identity";
import { MessageProvider } from "../PA.Core/Basic/Messages";
import { ProgressProvider } from "../PA.Core/Basic/Progress";
import { IMessageProvider, IProgressProvider, MessageType } from "../PA.Core/PA.Core.IF.Basic";
import { IBusinessObject } from "../PA.Core/PA.Core.IF.BO";
import { ESDataStore } from "../PA.Core/lib/ESDataStore";
import { ESRecord } from "../PA.Core/lib/ESRecord";
import { CreateOptions, ESRecordResolver } from "../PA.Core/lib/ESRecordResolver";
import { PAdbContext } from "../PA.Core/PAdbContext";
import {
    esWB_BatchProcess, esWB_Domain, esWB_Domain_Item
} from "../PA.Core/PAdbSchema";
import {
    WB_BatchProcessRow, WB_DomainItemRow, WB_DomainRow
} from "../PA.Core/PAdbSchemaRows";
import { IwbBatchProcess } from "../PA.Core/PAdbSchemaRowsIF";
import { PAEnvironment } from "../PA.Core/PAEnvironment";
import { IJobParam } from "../PA.Job.Core/PA.Job.IF.JobParam";
import { JobParamManager } from "../PA.Job.Core/JobParamManager";

export class BatchProcessBO
    implements IBusinessObject {

    //<editor-fold desc = "Properties and Constructor">
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOption: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_BatchProcess: WB_BatchProcessRow;
    drWB_DomainItem: WB_DomainItemRow;

    constructor(options?: any) {
        this.CreateOptions = options || {};
        if ("dbContext" in this.CreateOptions) {
            this.dbContext = this.CreateOptions.dbContext;
        }

        this.Identity = new IdentityProvider({ id: 0, name: "New Batch Process Type" });

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider("Loading", this);

        this.ValidateOption = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_BatchProcess = null;
        this.drWB_DomainItem = null;
    }

    //</editor-fold>

    get WB_BatchProcess(): WB_BatchProcessRow {
        return this.drWB_BatchProcess;
    }

    //<editor-fold desc = "IBusinessObject operations">

    async LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;

        self.LoadProgressProvider.start("Creating Batch Process");
        try {
            self.LoadOptions = options || {};

            if ("dbContext" in self.LoadOptions) {
                self.dbContext = self.LoadOptions.dbContext;
            } else {
                self.dbContext = new PAdbContext();
            }

            if (this.dbContext == null) {
                const sError = "Unable to determine dbContext for new Batch Process.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                throw sError;
            }

            let aNewRow: WB_BatchProcessRow = <WB_BatchProcessRow>await self.dbContext.createRecordWithKey(esWB_BatchProcess);
            aNewRow.setDefaults();
            self.drWB_BatchProcess = aNewRow;
            self.dbContext.add(esWB_BatchProcess, aNewRow);

            self.Identity.setAll(aNewRow.cif.WB_BATCHPROCESS_ID, aNewRow.cif.BATCHPROCESS_ID, aNewRow.cif.BATCHPROCESS_NAME);
            self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")"
            );
            this.Identity.listenToRecordUpdates(self.drWB_BatchProcess,
                "WB_BATCHPROCESS_ID",
                "BATCHPROCESS_ID",
                "BATCHPROCESS_NAME");

            self.LoadProgressProvider.finish();
            self._bIsLoaded = true;
            self._OnLoadComplete = null;
            await self.IJobParam_OnLoadComplete();
            return self;
        } catch (err) {
            self.LoadProgressProvider.exception(err);
            throw err;
        }

    }

    async Load(anId: number | string, options?: object): Promise<IBusinessObject> {
        const self = this;
        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")");
        try {
            if (self.bIsLoaded) {
                const sError = "Batch Process already loaded.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                throw sError;
            }
            self.LoadOptions = options || {};

            if ("dbContext" in self.LoadOptions) {
                self.dbContext = self.LoadOptions.dbContext;
            } else if (self.dbContext == null) {
                self.dbContext = new PAdbContext();
            }

            if (this.dbContext == null) {
                const sError = "Unable to determine dbContext for Batch Process.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                throw sError;
            }

            if ("RecordResolver" in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = Promise.resolve();
            } else {
                recordResolver = BatchProcessBO.createRecordResolver({ dbContext: self.dbContext });
                recordResolverLoadPromise = BatchProcessBO.setupRecordResolver(recordResolver, [anId]);
            }

            await recordResolverLoadPromise;

            const aLoadRow: WB_BatchProcessRow = <any>recordResolver.get(esWB_BatchProcess, anId);

            if (_.isEmpty(aLoadRow)) {
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception("Id not found.");
                return self;
            }

            self.drWB_BatchProcess = aLoadRow;
            self.Identity.setAll(aLoadRow.cif.WB_BATCHPROCESS_ID, aLoadRow.cif.BATCHPROCESS_ID, aLoadRow.cif.BATCHPROCESS_NAME);
            self.LoadProgressProvider.setName("Loading " + this.Identity.name + "(" + this.Identity.id_ui + ")"
            );
            this.Identity.listenToRecordUpdates(self.drWB_BatchProcess,
                "WB_BATCHPROCESS_ID",
                "BATCHPROCESS_ID",
                "BATCHPROCESS_NAME");


            let arrDom: WB_DomainRow[] = await self.dbContext.findAll(esWB_Domain, {
                where: {
                    DELETED_IND: { neq: "1" },
                    DOMAIN_ID: "ExternalBatchProcesses"
                }
            }, { with: [esWB_Domain_Item] });

            if (arrDom.length) {
                let domItemRows = _.filter(arrDom[0].WB_DomainItemRows, row => row.isActive && row.cif.DOMAIN_ITEM_ID == self.drWB_BatchProcess.cif.JOB_ID);
                if (domItemRows.length)
                    self.drWB_DomainItem = domItemRows[0];
            }
            self._bIsLoaded = true;
            self._OnLoadComplete = null;
            self.LoadProgressProvider.finish();
            await self.IJobParam_OnLoadComplete();
            return self;
        } catch (err: any) {
            self._OnLoadComplete = null;
            self.LoadProgressProvider.exception(err);
            throw err;
        }
    }

    get WB_DomainItemRow(): WB_DomainItemRow {
        return this.drWB_DomainItem;
    }

    set WB_DomainItemRow(value: WB_DomainItemRow) {
        this.drWB_DomainItem = value;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    async Validate(options?: any): Promise<IBusinessObject> {
        const self = this;
        self._bIsValidated = true;
        self.ValidateMessageProvider.clearMessages();
        self.ValidateProgressProvider.start("Validating " + self.WB_BatchProcess.cif.BATCHPROCESS_NAME + "(" + self.WB_BatchProcess.cif.BATCHPROCESS_ID + ")");
        self.ValidateOption = options || {};

        if (self.dbContext == undefined) {
            const sError = "Unable to determine dbContext to validate Batch Process.";
            self.ValidateProgressProvider.exception(sError);
            self.ValidateMessageProvider.addMessage({
                type: MessageType.Error,
                title: sError,
                description: sError,
                code: "dbContext"
            });
            self._bIsValidated = false;
            return self;
        }

        if (_.isEmpty(self.WB_BatchProcess.cif.BATCHPROCESS_NAME)) {
            let sError = "Batch Process Name cannot be empty";
            self.ValidateMessageProvider.addMessage({
                type: MessageType.Error,
                title: sError,
                description: sError,
                code: "NameEmpty"
            });
        }

        if (_.isEmpty(self.WB_BatchProcess.cif.JOB_ID)) {
            let sError = "External Process cannot be empty";
            self.ValidateMessageProvider.addMessage({
                type: MessageType.Error,
                title: sError,
                description: sError,
                code: "JobEmpty"
            });
        }

        if (!(self.WB_BatchProcess.cif.WB_BATCHSCOPE_ID > 0)) {
            let sError = "Batch Scope cannot be empty";
            self.ValidateMessageProvider.addMessage({
                type: MessageType.Error,
                title: sError,
                description: sError,
                code: "ScopeEmpty"
            });
        }

        let processes = await self.dbContext.findAll(esWB_BatchProcess, {
            where: {
                DELETED_IND: { neq: "1" },
                BATCHPROCESS_NAME: self.WB_BatchProcess.cif.BATCHPROCESS_NAME,
                WB_BATCHPROCESS_ID: { neq: self.WB_BatchProcess.cif.WB_BATCHPROCESS_ID }
            }
        });
        if (!_.isEmpty(processes)) {
            let sError = "Batch Process Name cannot be duplicate";
            self.ValidateMessageProvider.addMessage({
                type: MessageType.Error,
                title: sError,
                description: sError,
                code: "NameDuplicate"
            });
        }

        processes = await self.dbContext.findAll(esWB_BatchProcess, {
            where: {
                DELETED_IND: { neq: "1" },
                JOB_ID: self.WB_BatchProcess.cif.JOB_ID,
                WB_BATCHSCOPE_ID: self.WB_BatchProcess.cif.WB_BATCHSCOPE_ID,
                WB_BATCHPROCESS_ID: { neq: self.WB_BatchProcess.cif.WB_BATCHPROCESS_ID }
            }
        });

        if (!_.isEmpty(processes)) {
            let sError = "Duplicate Batch Process Template of " + _.join(_.map(processes, 'BATCHPROCESS_NAME'), ", ")
            self.ValidateMessageProvider.addMessage({
                type: MessageType.Error,
                title: sError,
                description: sError,
                code: "JobDuplicate"
            });

        }
        self._bIsValidated = (self.ValidateMessageProvider.countMessages(MessageType.Error) == 0);
        self.ValidateProgressProvider.finish();
        return self;
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;

        try {
            const wbBatchProcess = <IwbBatchProcess>self.drWB_BatchProcess;
            this.SaveProgressProvider.start(
                "Saving " + wbBatchProcess.BATCHPROCESS_NAME + "(" + wbBatchProcess.BATCHPROCESS_ID + ")"
            );

            if (self.dbContext == null) {
                self._bIsSaved = false;
                const sError = "Unable to determine dbContext to Save Batch Process.";
                self.SaveProgressProvider.exception(sError);
                return self;
            }

            self.SaveOptions = options || {};

            await self.Validate(options);
            if (!self.bIsValidated) {
                self._bIsSaved = false;
                self.SaveProgressProvider.finish();
                return;
            }

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;

            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            await saveFunction(esWB_BatchProcess, self.drWB_BatchProcess, {});

            if (!self.JobParam) {
                if (self.WB_BatchProcess.isActive)
                    await self.IJobParam_OnLoadComplete();
            }

            if (self.JobParam) {
                self.JobParam.id_ui = self.WB_BatchProcess.cif.BATCHPROCESS_ID
                self.JobParam.name = self.WB_BatchProcess.cif.BATCHPROCESS_NAME
                await self.JobParam.Save(bBatch);
            }

            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                });
            } else {
                self._bIsSaved = true;
            }

            self.SaveProgressProvider.finish();

        } catch (err) {
            self.SaveProgressProvider.exception(err);
        }

        return self;
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private async DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbBatchProcess = <IwbBatchProcess>self.drWB_BatchProcess;

        try {

            self.DeleteMessageProvider.clearMessages();
            self.DeleteProgressProvider.start(
                "Deleting " + wbBatchProcess.BATCHPROCESS_NAME + "(" + wbBatchProcess.BATCHPROCESS_ID + ")"
            );

            self.drWB_BatchProcess.markDeleted();

            if (bSaveNow)
                await self.SaveUsing(false);

            self._bIsDeleted = true;
            self.DeleteProgressProvider.finish();
        } catch (err) {
            self.DeleteProgressProvider.exception(err);
        }

        return self;
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    //</editor-fold>


    //<editor-fold desc = "Job Param">
    _jobParam: IJobParam = undefined;

    async IJobParam_OnLoadComplete(): Promise<void> {
        const self = this;

        if(self._jobParam)
            return;

        const jpMgr = new JobParamManager();
        const sourceWbId = this.WB_BatchProcess.cif.WB_BATCHPROCESS_ID;
        const jobParamId = this.WB_BatchProcess.cif.BATCHPROCESS_ID;
        const jobParamName = this.WB_BatchProcess.cif.BATCHPROCESS_NAME;

        const jobProcessorType = "BatchProcess";

        const jobParam = await jpMgr.findOrCreate({
            dbContext: this.dbContext,
            JobSchedulerModuleId: "BatchProcess",

            sourceType: "BatchProcess",
            sourceWbId: sourceWbId,

            appExecId: "BatchProcessTemplate",
            wbTargetId: sourceWbId,

            wbMasterDataSetId: 0,
            wbCompanyId: 0,
            wbSalesOrgId: 0,

            JobParamId: jobParamId,
            JobParamName: jobParamName,
            JobProcessorType: jobProcessorType
        });

        this._jobParam = jobParam;

    }

    get JobParam(): IJobParam {
        return this._jobParam;
    }

    //<editor-fold>


    //<editor-fold desc = "IRecord Resolver operations <static>">

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        const opts = boLoadOptions || {};
        let dbContext: PAdbContext;
        if ("dbContext" in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const BatchProcessType_options: CreateOptions = {
            standardConditions: {
                [esWB_BatchProcess]: { "DELETED_IND": { "notIn": ["1"] } }
            },
            dctIncludeBelongsToNamedRelations: {}
        };

        return new ESRecordResolver(dbContext, BatchProcessType_options);
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: (string | number)[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            recordResolver.findAllWithRelationships(esWB_BatchProcess, ids)
                .then(() => {
                    resolve();
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }


    //</editor-fold>

}

PAEnvironment.registerBusinessObjectClass(BatchProcessBO, "BatchProcessBO", "Batch Process");
